import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Flex, HStack, Icon, useColorModeValue, VStack } from '@chakra-ui/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FaCreditCard } from 'react-icons/fa';
import Sidebar from '../components/dashboard/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { getUIPreferences, updateUIPreferences } from '../utils/localStorage';
import { isAdmin } from '../utils/permissions';
import ReadOnlyBanner from '../components/ReadOnlyBanner';
import UsageLimitBanner from '../components/UsageLimitBanner';
import TrialBanner from '../components/TrialBanner';
import GlobalSearch from '../components/GlobalSearch';
import NotificationBell from '../components/NotificationBell';
import DashboardFooter from '../components/dashboard/DashboardFooter';

const DashboardLayout = () => {
  const { logout } = useAuth();
  const { authenticatedUser } = useAuthenticatedUser();
  const navigate = useNavigate();
  const showBillingLink = isAdmin(authenticatedUser);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(() => {
    const prefs = getUIPreferences();
    return prefs.sidebarMinimized;
  });

  const handleToggleSidebar = () => {
    const newState = !isSidebarMinimized;
    setIsSidebarMinimized(newState);
    updateUIPreferences({ sidebarMinimized: newState });
  };

  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.900, purple.900)'
  );
  const headerButtonBg = useColorModeValue('white', 'gray.800');

  const mainContentRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    mainContentRef.current?.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Flex>
        <Sidebar
          activeTab={''} // Not needed for new routing, but kept for compatibility
          onTabChange={() => {}} // Not needed for new routing
          onLogout={logout}
          userInfo={{
            firstName: authenticatedUser?.firstName,
            lastName: authenticatedUser?.lastName,
            role: authenticatedUser?.role,
          }}
          authenticatedUser={authenticatedUser}
          isMinimized={isSidebarMinimized}
          onToggleMinimize={handleToggleSidebar}
        />
        <VStack flex="1">
          <Box
            ref={mainContentRef}
            flex="1"
            minH="100vh"
            overflowY="auto"
            transition="all 0.3s ease"
            w={isSidebarMinimized ? 'calc(100vw - 80px)' : 'calc(100vw - 280px)'}
            p={'50px 35px 10px 35px'}
            display="flex"
            flexDirection="column"
          >
            <HStack justify="flex-end" spacing={3} mb={4} display={{ base: 'none', md: 'flex' }}>
              {showBillingLink && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Icon as={FaCreditCard} />}
                  onClick={() => navigate('/dashboard/billing')}
                  bg={headerButtonBg}
                >
                  Plan & billing
                </Button>
              )}
              <GlobalSearch />
              <NotificationBell />
            </HStack>
            <TrialBanner />
            <UsageLimitBanner />
            <ReadOnlyBanner />
            <Box flex="1">
              <Outlet />
            </Box>
          </Box>
          <DashboardFooter />
        </VStack>
      </Flex>
    </Box>
  );
};

export default DashboardLayout;
